import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import FileInputField from 'components/common/formikForm/fileInputField';
import FormField from 'components/common/formikForm/formField';
import SubmitButton from 'components/common/formikForm/SubmitButton';
import TitleTooltip from 'components/v2/common/titleTooltip';

import api from 'api';
import { Role } from 'data/constants/roles';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import type { AppDispatch } from 'redux/store';
import type {
    DocumentTemplateFormValues,
    DocumentTemplateRecord,
    OwnershipScope,
    ProviderOption,
} from 'types/servicesDocuments';
import { appendDocumentTemplateFormData } from 'utils/servicesDocuments';
import {
    documentTemplateFormInitialValues,
    documentTemplateFormValidationSchema,
} from 'utils/validations/servicesDocuments';
import { getErrorMessage } from 'utils/v2/errorHelper';

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void | Promise<void>;
    initialData?: DocumentTemplateRecord | null;
    userRole: string;
    defaultScope: OwnershipScope;
    defaultProviderId: number | '';
    providerOptions: ProviderOption[];
}

const mapInitialValues = (
    initialData: DocumentTemplateRecord | null | undefined,
    defaults: Pick<DocumentTemplateFormValues, 'scope' | 'service_provider_id'>
): DocumentTemplateFormValues => ({
    ...documentTemplateFormInitialValues,
    ...defaults,
    name_en: initialData?.name_en || '',
    name_fr: initialData?.name_fr || '',
    description_en: initialData?.description_en || '',
    description_fr: initialData?.description_fr || '',
    is_form: Boolean(initialData?.is_form),
    fields_json: initialData?.fields_json
        ? typeof initialData.fields_json === 'string'
            ? initialData.fields_json
            : JSON.stringify(initialData.fields_json, null, 2)
        : '',
    document_type: initialData?.document_type || '',
    category: initialData?.category || '',
    country_code: initialData?.country_code || '',
    template: null,
    sample: null,
});

const DocumentTemplateFormDialog = ({
    open,
    onClose,
    onSuccess,
    initialData,
    userRole,
    defaultScope,
    defaultProviderId,
    providerOptions,
}: Props) => {
    const dispatch = useDispatch<AppDispatch>();
    const { translate } = useTranslate();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [submitting, setSubmitting] = useState(false);
    const isEditMode = Boolean(initialData?.id);
    const isSuperSuperAdmin = userRole === Role.SUPER_SUPER_ADMIN;

    const initialValues = useMemo(
        () =>
            mapInitialValues(initialData, {
                scope: isEditMode
                    ? initialData?.service_provider_id == null
                        ? 'universal'
                        : 'provider'
                    : defaultScope,
                service_provider_id: isEditMode
                    ? (initialData?.service_provider_id ?? '')
                    : defaultProviderId,
            }),
        [defaultProviderId, defaultScope, initialData, isEditMode]
    );

    const handleSubmit = async (values: DocumentTemplateFormValues) => {
        try {
            setSubmitting(true);
            const payload = new FormData();
            appendDocumentTemplateFormData(payload, values, {
                includeScope: !isEditMode,
            });

            if (isEditMode && initialData?.id) {
                await api.documentTemplates.updateDocumentTemplate(initialData.id, payload);
            } else {
                await api.documentTemplates.createDocumentTemplate(payload);
            }

            dispatch(setAlert({ message: translate('saveChanges'), type: 'success' }));
            await onSuccess();
            onClose();
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('somethingWentWrong')),
                    type: 'error',
                })
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
            <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={documentTemplateFormValidationSchema}
                onSubmit={handleSubmit}
            >
                {({ values, setFieldValue }) => (
                    <Box component={Form}>
                        <DialogTitle>
                            <TitleTooltip
                                title={translate(
                                    isEditMode ? 'editDocumentTemplate' : 'addDocumentTemplate'
                                )}
                            />
                        </DialogTitle>
                        <DialogContent
                            dividers
                            sx={{ maxHeight: 'min(70vh, 720px)', overflowY: 'auto' }}
                        >
                            <Stack spacing={1.25}>
                                {!isEditMode && isSuperSuperAdmin ? (
                                    <>
                                        <FormControl fullWidth size="small">
                                            <InputLabel id="template-form-scope-label">
                                                {translate('servicesDocuments.scope.ownershipAria')}
                                            </InputLabel>
                                            <Select
                                                labelId="template-form-scope-label"
                                                label={translate(
                                                    'servicesDocuments.scope.ownershipAria'
                                                )}
                                                value={values.scope}
                                                onChange={(event) =>
                                                    setFieldValue(
                                                        'scope',
                                                        event.target.value as OwnershipScope
                                                    )
                                                }
                                            >
                                                <MenuItem value="provider">
                                                    {translate('servicesDocuments.scope.provider')}
                                                </MenuItem>
                                                <MenuItem value="universal">
                                                    {translate('servicesDocuments.scope.universal')}
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                        {values.scope === 'provider' ? (
                                            <FormField
                                                type="autosuggest"
                                                name="service_provider_id"
                                                label="servicesDocuments.scope.selectProvider"
                                                options={providerOptions.map((option) => ({
                                                    label: option.name,
                                                    value: option.id,
                                                }))}
                                                required
                                                disabled={submitting}
                                            />
                                        ) : null}
                                    </>
                                ) : null}

                                <FormField
                                    size="small"
                                    name="name_en"
                                    label="servicesDocuments.fields.nameEn"
                                    disabled={submitting}
                                />
                                <FormField
                                    size="small"
                                    name="name_fr"
                                    label="servicesDocuments.fields.nameFr"
                                    disabled={submitting}
                                />
                                <FormField
                                    type="template"
                                    size="small"
                                    name="description_en"
                                    label="servicesDocuments.fields.descriptionEn"
                                    multiline
                                    minRows={2}
                                    disabled={submitting}
                                />
                                <FormField
                                    type="template"
                                    size="small"
                                    name="description_fr"
                                    label="servicesDocuments.fields.descriptionFr"
                                    multiline
                                    minRows={2}
                                    disabled={submitting}
                                />
                                <FormField
                                    size="small"
                                    type="select"
                                    name="country_code"
                                    label="servicesDocuments.fields.country"
                                    options={[
                                        {
                                            label: translate(
                                                'servicesDocuments.filters.anyCountry'
                                            ),
                                            value: '',
                                        },
                                        { label: 'CA', value: 'CA' },
                                        { label: 'AU', value: 'AU' },
                                        { label: 'UK', value: 'UK' },
                                    ]}
                                    required={values.scope === 'universal'}
                                    disabled={submitting}
                                />
                                <FormField
                                    size="small"
                                    type="select"
                                    name="document_type"
                                    label="servicesDocuments.filters.documentType"
                                    options={[
                                        { label: 'ORGANIZATION', value: 'ORGANIZATION' },
                                        { label: 'USER', value: 'USER' },
                                        { label: 'SERVICES_ALL', value: 'SERVICES_ALL' },
                                        { label: 'SERVICES_SELECTED', value: 'SERVICES_SELECTED' },
                                    ]}
                                    required
                                    disabled={submitting}
                                />
                                <FormField
                                    size="small"
                                    type="select"
                                    name="category"
                                    label="servicesDocuments.fields.category"
                                    options={[
                                        { label: '—', value: '' },
                                        { label: 'ADMINISTRATIVE', value: 'ADMINISTRATIVE' },
                                        { label: 'EDUCATION', value: 'EDUCATION' },
                                        { label: 'EMPLOYMENT', value: 'EMPLOYMENT' },
                                        {
                                            label: 'FAMILY_RELATIONSHIP_AND_CUSTODY',
                                            value: 'FAMILY_RELATIONSHIP_AND_CUSTODY',
                                        },
                                        { label: 'FINANCIAL', value: 'FINANCIAL' },
                                        { label: 'GOVERNMENT_FORM', value: 'GOVERNMENT_FORM' },
                                        {
                                            label: 'IDENTITY_AND_CIVIL_STATUS',
                                            value: 'IDENTITY_AND_CIVIL_STATUS',
                                        },
                                        { label: 'LANGUAGE', value: 'LANGUAGE' },
                                        {
                                            label: 'MEDICAL_AND_SECURITY',
                                            value: 'MEDICAL_AND_SECURITY',
                                        },
                                        { label: 'NOMINATION', value: 'NOMINATION' },
                                    ]}
                                    disabled={submitting}
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={values.is_form}
                                            onChange={(_, checked) => {
                                                setFieldValue('is_form', checked);
                                                if (!checked) {
                                                    setFieldValue('fields_json', '');
                                                }
                                            }}
                                        />
                                    }
                                    label={translate('servicesDocuments.fields.isForm')}
                                />
                                {values.is_form ? (
                                    <FormField
                                        name="fields_json"
                                        label="servicesDocuments.fields.fieldsJson"
                                        multiline
                                        minRows={4}
                                        disabled={submitting}
                                    />
                                ) : null}
                                <FileInputField
                                    name="template"
                                    label="servicesDocuments.fields.templateFile"
                                    accept=".pdf,.doc,.docx"
                                    existingUri={initialData?.template_uri}
                                    disabled={submitting}
                                />
                                <FileInputField
                                    name="sample"
                                    label="servicesDocuments.fields.sampleFile"
                                    accept=".pdf,.doc,.docx"
                                    existingUri={initialData?.sample_uri}
                                    disabled={submitting}
                                />
                            </Stack>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={onClose} color="inherit" disabled={submitting}>
                                {translate('cancel')}
                            </Button>
                            <SubmitButton loading={submitting}>save</SubmitButton>
                        </DialogActions>
                    </Box>
                )}
            </Formik>
        </Dialog>
    );
};

export default DocumentTemplateFormDialog;
